import { ColorEnum, GameOverResultEnum } from '@/enums/chessEnum';
import type { ChessType } from '#/chess';
import { RANDOM_COLOR } from '@/constant/chess';
import { GameOverWayEnum } from '@/enums/chessEnum';
import { useI18n } from '@/hooks/web/useI18n';

const { t } = useI18n();

interface Option<T> {
    value: T;
    label: string;
    icon?: string;
    index?: number;
}

export type OptionList<T> = Array<Option<T>>;

export type ColorOptionList = OptionList<ColorEnum | typeof RANDOM_COLOR>;

export type LevelOptionList = OptionList<ChessType.LevelParam>;

export const colorOptionList: ColorOptionList = [
    {
        label: t('components.game.orientation.black'),
        value: ColorEnum.BLACK,
        icon: 'color-black',
        index: 0,
    },
    {
        label: t('components.game.orientation.white'),
        value: ColorEnum.WHITE,
        icon: 'color-white',
        index: 1,
    },
    {
        label: t('common.random'),
        value: RANDOM_COLOR,
        icon: 'random',
        index: 2,
    },
];

export const timeOptionList: OptionList<number> = [1, 3, 5, 10, 15, 20, 25, 30, 60, 90].map(
    (item): Option<number> => ({ value: item, label: item + '' }),
);

export const incOptionList: OptionList<number> = [0, 1, 2, 3, 5, 10, 30].map(
    (item): Option<number> => ({ value: item, label: item + '' }),
);

export const levelOptionList: LevelOptionList = [
    {
        label: 'L1',
        value: {
            level: '1',
            Hash: 4,
            'Slow Mover': 10,
            UCI_Elo: 1320,
            depth: 1,
            randomRatio: 0.8,
        },
    },
    {
        label: 'L2',
        value: {
            level: '2',
            Hash: 4,
            'Slow Mover': 10,
            UCI_Elo: 1330,
            depth: 1,
            randomRatio: 0.5,
        },
    },
    {
        label: 'L3',
        value: {
            level: '3',
            Hash: 8,
            'Slow Mover': 20,
            UCI_Elo: 1350,
            depth: 2,
            randomRatio: 0.3,
        },
    },
    {
        label: 'L4',
        value: {
            level: '4',
            Hash: 8,
            'Slow Mover': 20,
            UCI_Elo: 1375,
            depth: 2,
            randomRatio: 0.1,
        },
    },
    {
        label: 'L5',
        value: {
            level: '5',
            Hash: 8,
            'Slow Mover': 30,
            UCI_Elo: 1400,
            depth: 5,
            randomRatio: 0.05,
        },
    },
    {
        label: 'L6',
        value: {
            level: '6',
            Hash: 16,
            'Slow Mover': 40,
            UCI_Elo: 1500,
            depth: 5,
            randomRatio: 0,
        },
    },
    {
        label: 'L7',
        value: {
            level: '7',
            Hash: 16,
            'Slow Mover': 50,
            UCI_Elo: 1600,
            depth: 6,
            randomRatio: 0,
        },
    },
    {
        label: 'L8',
        value: {
            level: '8',
            Hash: 16,
            'Slow Mover': 60,
            UCI_Elo: 1700,
            depth: 7,
            randomRatio: 0,
        },
    },
    {
        label: 'L9',
        value: {
            level: '9',
            Hash: 24,
            'Slow Mover': 70,
            UCI_Elo: 1850,
            depth: 10,
            randomRatio: 0,
        },
    },
    {
        label: 'L10',
        value: {
            level: '10',
            Hash: 24,
            'Slow Mover': 80,
            UCI_Elo: 2000,
            depth: 12,
            randomRatio: 0,
        },
    },
    {
        label: 'L11',
        value: {
            level: '11',
            Hash: 24,
            'Slow Mover': 90,
            UCI_Elo: 2150,
            depth: 14,
            randomRatio: 0,
        },
    },
    {
        label: 'L12',
        value: {
            level: '12',
            Hash: 32,
            'Slow Mover': 100,
            UCI_Elo: 2300,
            depth: 16,
            randomRatio: 0,
        },
    },
];

export const defaultRobotConfig: ChessType.robotConfig = {
    level: levelOptionList.at(0)!.value,
    color: ColorEnum.WHITE,
    time: timeOptionList.at(2)!.value,
    inc: incOptionList.at(3)!.value,
};

export const defaultRobotGameOverConfig: Record<
    Exclude<GameOverWayEnum, 'CHECKMATE'>,
    { result: GameOverResultEnum; reason: string }
> = {
    [GameOverWayEnum.DRAW]: {
        result: GameOverResultEnum.DRAW,
        reason: t('components.game.reason.50moveRule'),
    },
    [GameOverWayEnum.INSUFFICIENT_MATERIAL]: {
        result: GameOverResultEnum.DRAW,
        reason: t('components.game.reason.insufficientMaterial'),
    },
    [GameOverWayEnum.STALEMATE]: {
        result: GameOverResultEnum.DRAW,
        reason: t('components.game.reason.stalemate'),
    },
    [GameOverWayEnum.THREEFOLD_REPETITION]: {
        result: GameOverResultEnum.DRAW,
        reason: t('components.game.reason.threefoldRepetition'),
    },
};
